// CRM 훅 계층 — 페이지와 api/crm.ts 사이의 단일 경계.
//
// 페이지는 이 파일만 import 한다. api/* · mocks/* 직접 참조 금지.
// D1 옵션 A (customers ↔ applications 분리) 에 따른 ID 타입(number) ·
// 2분리 shape 은 전부 여기서 흡수한다.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as api from "../api/crm";
import {
  fetchFormDefaults,
  fetchPlanChangeExpr,
  updatePlanChangeExpr,
} from "../api/tenants";
import { RETAILER_FIELD_TABS } from "../../shared/retailer-form-fields";
import { applyRetailerDefaults } from "../components/crm/retailerDefaults";
import type { DealerInvoiceConfirmation } from "../../shared/dealer-invoice-confirmation";

export const crmKeys = {
  customers: (filter: api.CustomerFilter) => ["crm", "customers", filter] as const,
  customer: (id: number) => ["crm", "customer", id] as const,
  applications: (filter: api.ApplicationFilter) =>
    ["crm", "applications", filter] as const,
  applicationsByCustomer: (customerId: number) =>
    ["crm", "applications", "by-customer", customerId] as const,
  application: (id: number) => ["crm", "application", id] as const,
  applicationsExport: () => ["crm", "applications", "export"] as const,
  /** 정산관리 목록(개통월별). ["crm","applications"] 접두라 신청서 수정 후 함께 무효화된다. */
  settlements: (month: string) =>
    ["crm", "applications", "settlements", month] as const,
  /** 정산관리 화면만의 거래 대리점 다섯 줄/순서. */
  dealerSettlementLayout: () => ["crm", "dealerSettlementLayout"] as const,
  /** 정산관리의 선택 월별 대리점 계산서 발행확인. */
  dealerInvoiceConfirmations: (month: string) =>
    ["crm", "dealerInvoiceConfirmations", month] as const,
  // 매장이 저장해 둔 내보내기 항목 구성(키 배열·순서). null = 저장 이력 없음.
  exportColumns: () => ["crm", "applications", "exportColumns"] as const,
  stageCounts: () => ["crm", "stageCounts"] as const,
  pipelineCounts: () => ["crm", "pipelineCounts"] as const,
  // task 312 — 파이프라인 단계 정의(시스템 뼈대 + 가운데 커스텀 띠).
  pipelineStages: () => ["crm", "pipelineStages"] as const,
  // 공개 폼은 #134 에서 mocks → api 로 스왑 예정. key 는 그대로 유지된다.
  publicForm: (token: string) => ["crm", "publicForm", token] as const,
  // 요금제 변경 가능일 계산식 — 매장당 하나. 모달/상세의 날짜 계산 위젯이 공유한다.
  planChangeExpr: () => ["crm", "planChangeExpr"] as const,

  // ── 여러 화면이 공유하는 키 중앙화 (task 311) ──────────────────────────
  // 아래는 원래 컴포넌트/페이지에 문자열 리터럴로 흩어져 있던 키들이다. 같은 데이터를
  // 4~7개 파일이 각자 박아 쓰다 한 곳만 어긋나면 무효화가 조용히 빗나갔다(이게 D23의
  // form-defaults 단/복수 사고). 배열은 기존과 한 글자도 다르지 않게 그대로 옮긴다.
  /** 매장 정산식 설정(정산금·마진 커스텀). 신청서 모달·상세·정산식 페이지가 공유. */
  settlementConfig: () => ["crm", "settlement-config"] as const,
  /** 판매점 입력 항목 설정(커스텀·숨김·라벨). 신청서 모달·상세·내보내기·서식상세·설정 공유. */
  retailerFieldConfig: () => ["crm", "retailer-field-config"] as const,
  /** 고객 입력폼 항목 설정. 신청서 모달·상세·내보내기·서식상세·고객폼 설정 공유. */
  customerFormConfig: () => ["crm", "customer-form-config"] as const,
  /** 마스터데이터(요금제·대리점·고객유형·모델·부가 등). kind 별 목록. 콤보박스 전반 공유. */
  master: (kind: string) => ["crm", "master", kind] as const,
  /** 단말기 모델 카탈로그. 모델 콤보박스·신청서 모달 자동채움 공유. */
  phoneModels: () => ["crm", "phoneModels"] as const,
  /** 통신사별 요금제 카탈로그. 요금제 콤보박스·신청서 모달 공시지원금 조회 공유. */
  plans: (carrier: string | null | undefined) => ["crm", "plans", carrier] as const,
  // ── 대리점 부가서비스 (task 311 중앙화: 인라인 키를 팩토리로) ───────────────
  // 대리점 부가서비스 관리 화면(CrmDealerAddonsPage)·신청서 부가 토글(DealerAddonToggle)이
  // 같은 데이터를 각자 인라인 문자열로 박아 쓰던 것을 팩토리로 모은다. 배열 모양은
  // 기존 인라인과 한 글자도 다르지 않게 그대로 옮긴다(키가 어긋나면 무효화가 빗나간다).
  /** 한 대리점이 보유한 부가서비스 목록. dealerId 별. 관리 화면·신청서 토글 공유. */
  dealerAddons: (dealerId: number) => ["crm", "dealerAddons", dealerId] as const,
  /** 전 통신사 부가서비스 카탈로그(제로노트). 한 번에 받아 프론트 검색. 단일("all") 슬롯. */
  addonCatalog: () => ["crm", "addonCatalog", "all"] as const,
  /** 전 통신사 보험 카탈로그(제로노트). addonCatalog 와 별도 API·별도 쿼리. */
  insuranceCatalog: () => ["crm", "insuranceCatalog", "all"] as const,
  /** 한 고객의 작성 서식 목록. 신청서 상세·서식 섹션 공유. customerId 미정(null) 가능. */
  renderedForms: (customerId: number | null) =>
    ["crm", "rendered-forms", customerId] as const,
  /** 한 신청서의 정산 근거 목록. 신청서 상세 정산 근거 패널 전용. */
  settlementEvidence: (applicationFormId: number | null) =>
    ["crm", "settlement-evidence", applicationFormId] as const,
  /** 한 신청서의 서류함 목록. 개통 신청서 수정 모달 서류함 섹션 전용 (스크린챗 #4). */
  applicationDocuments: (applicationFormId: number | null) =>
    ["crm", "application-documents", applicationFormId] as const,
  /** 매장의 서류 종류 목록. 서류함 종류 드롭다운 공유(단일 슬롯). */
  documentTypes: () => ["crm", "document-types"] as const,
  /** 서식 템플릿 목록. 서식 카탈로그·작성 화면·템플릿 선택 모달 공유.
   *  신청서를 지정해 부르면 그 신청서 매장 서식까지 합쳐 오므로 목록 자체가 달라진다 →
   *  신청서 id 를 키에 실어 캐시를 분리한다(#175). 안 주면 종전 키 그대로. */
  formTemplates: (applicationFormId?: number) =>
    ["crm", "form-templates", ...(applicationFormId ? ["af", applicationFormId] : [])] as const,
  /** 서식 템플릿 단건. id 는 선택 전(null)일 수 있어 키만 만들고 조회는 enabled 로 가드. */
  formTemplate: (id: number | null) => ["crm", "form-templates", id] as const,
  /** 서식 템플릿 미리보기 이미지. */
  formTemplatePreview: (id: number | null) =>
    ["crm", "form-templates", id, "preview"] as const,
  /** 서식 템플릿 슬롯 매핑. */
  formTemplateMapping: (id: number | null) =>
    ["crm", "form-templates", id, "mapping"] as const,
  /** 서식 템플릿 버전 이력. */
  formTemplateVersions: (id: number | null) =>
    ["crm", "form-templates", id, "versions"] as const,
};

// ─── Queries ─────────────────────────────────────────

export function useCustomers(filter: api.CustomerFilter = {}) {
  return useQuery({
    queryKey: crmKeys.customers(filter),
    queryFn: () => api.fetchCustomers(filter),
  });
}

export function useCustomer(id: number | null | undefined) {
  return useQuery({
    queryKey: crmKeys.customer(id ?? 0),
    queryFn: () => api.fetchCustomer(id as number),
    enabled: id != null && Number.isFinite(id),
  });
}

// 고객현황 목록은 고객이 모바일 입력폼을 제출하면 이름·연락처·진행상태가 바뀐다.
// 자동 갱신은 useRealtimeApplications(같은 매장 채널 구독)가 신청서·고객·현황 키만 골라
// 무효화하는 방식으로 담당한다 —
// 과거의 60초 주기 polling 은 Realtime 도입 후 중복이라 제거했다(토큰 만료 경계 노출만
// 늘렸다). 사용자 액션 직후엔 mutation 의 invalidate(["crm"]) 로 즉시 반영된다.
// 캐시에는 { applications, truncated } 한 벌을 두고 화면엔 배열만 내준다. 같은 키를 쓰는
// 곳은 모두 이 두 훅을 거쳐야 캐시 모양이 어긋나지 않는다.
export function useApplicationPage(filter: api.ApplicationPageFilter, enabled = true) {
  const query = useQuery({
    queryKey: ["crm", "applications", "page", filter],
    queryFn: ({signal}) => api.fetchApplicationPage(filter, signal),
    enabled,
  });
  return { ...query, pageData: query.data, data: query.data?.applications };
}
export function useApplicationSummary(id: number) {
  const query = useApplicationPage({ view: "all", ids: [id], metadata: false }, id > 0);
  return { ...query, data: query.data?.[0] };
}

export function useApplications(filter: api.ApplicationFilter = {}) {
  return useQuery({
    queryKey: crmKeys.applications(filter),
    queryFn: () => api.fetchApplicationList(filter),
    select: selectApplications,
  });
}

/** 서버 상한에서 목록이 잘렸으면 상한 숫자, 아니면 null — «일부만 보인다» 띠용. */
export function useApplicationListTruncatedAt(filter: api.ApplicationFilter = {}) {
  const q = useQuery({
    queryKey: crmKeys.applications(filter),
    queryFn: () => api.fetchApplicationList(filter),
    select: selectTruncatedAt,
  });
  return q.data ?? null;
}

const selectApplications = (list: api.ApplicationList) => list.applications;
const selectTruncatedAt = (list: api.ApplicationList) =>
  list.truncated ? (list.limit ?? list.applications.length) : null;

// 정산관리 화면 — 개통월별 정산완료 목록 + 건별 금액(서버 계산). 폴링 없음:
// 상태 전이 mutation 과 Realtime 이 ["crm","applications"] 를 무효화하면 함께 갱신된다.
export function useSettlements(month: string) {
  return useQuery({
    queryKey: crmKeys.settlements(month),
    queryFn: () => api.fetchSettlements(month),
  });
}

/** 신청서의 개통대리점 명단과 같은 dealer 마스터 목록. */
export function useDealerMasters() {
  return useQuery({
    queryKey: crmKeys.master("dealer"),
    queryFn: () => api.listMasterData("dealer") as Promise<api.DealerMaster[]>,
    staleTime: 10_000,
  });
}

export function useDealerSettlementLayout() {
  return useQuery({
    queryKey: crmKeys.dealerSettlementLayout(),
    queryFn: api.fetchDealerSettlementLayout,
  });
}

export function useSaveDealerSettlementLayout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.saveDealerSettlementLayout,
    onSuccess: (layout) => qc.setQueryData(crmKeys.dealerSettlementLayout(), layout),
  });
}

export function useDealerInvoiceConfirmations(month: string) {
  return useQuery({
    queryKey: crmKeys.dealerInvoiceConfirmations(month),
    queryFn: () => api.fetchDealerInvoiceConfirmations(month),
  });
}

export function useSetDealerInvoiceConfirmation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.setDealerInvoiceConfirmation,
    onSuccess: (confirmation, input) => {
      qc.setQueryData<DealerInvoiceConfirmation[]>(
        crmKeys.dealerInvoiceConfirmations(input.month),
        (current = []) => {
          const withoutDealer = current.filter(
            (item) => item.dealerName !== input.dealerName,
          );
          return confirmation ? [...withoutDealer, confirmation] : withoutDealer;
        },
      );
    },
  });
}

// 신청서 데이터 내보내기 화면 — 미리보기·항목 카탈로그용 전체 행. 자주 바뀌지 않아 폴링 없음.
export function useApplicationsExport() {
  return useQuery({
    queryKey: crmKeys.applicationsExport(),
    queryFn: () => api.fetchApplicationsExport(),
  });
}

// 매장별로 DB(tenants.export_column_config)에 저장된 내보내기 항목 구성.
export function useExportColumns() {
  return useQuery({
    queryKey: crmKeys.exportColumns(),
    queryFn: api.fetchExportColumns,
  });
}

// 내보내기 항목 구성 저장 — 화면에서 항목·필터를 바꿀 때마다 디바운스로 자동 호출된다.
export function useSaveExportColumns() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { columns: string[]; filterColumns: string[] }) =>
      api.saveExportColumns(v.columns, v.filterColumns),
    onSuccess: (_, v) =>
      qc.setQueryData<api.ExportColumnConfig>(crmKeys.exportColumns(), {
        columns: v.columns,
        filterColumns: v.filterColumns,
      }),
  });
}

export function useApplicationsByCustomer(customerId: number | null | undefined) {
  return useQuery({
    queryKey: crmKeys.applicationsByCustomer(customerId ?? 0),
    queryFn: () => api.fetchApplicationsByCustomer(customerId as number),
    enabled: customerId != null && Number.isFinite(customerId),
  });
}

// 신청서 단건 — 신청서 디테일 페이지에서 사용. customers JOIN 이 빠진 raw 응답이라
// 이름·전화 등은 별도 useCustomer 로 채워야 한다.
export function useApplication(id: number | null | undefined) {
  return useQuery({
    queryKey: crmKeys.application(id ?? 0),
    queryFn: () => api.fetchApplication(id as number),
    enabled: id != null && Number.isFinite(id),
  });
}

export function useStageCounts() {
  return useQuery({
    queryKey: crmKeys.stageCounts(),
    queryFn: api.fetchStageCounts,
  });
}

// D6 (task 252) — 파이프라인 1급 상태 카운트. 현황판이 표시.
// 주기 polling 은 제거했다(Realtime + mutation invalidate 가 자동 갱신을 담당).
// 카드 닫기·issue 토글·advance 등 사용자 액션 직후엔 invalidate(["crm"]) 로 즉시 갱신되고,
// 다른 탭을 보다 돌아왔을 때를 위한 안전망으로 windowFocus refetch + staleTime 을 명시한다.
export function usePipelineCounts() {
  return useQuery({
    queryKey: crmKeys.pipelineCounts(),
    queryFn: api.fetchPipelineCounts,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });
}

// task 312 — 유효 단계 목록(시스템+가운데). 뱃지·현황판·진행바가 색·라벨·순서를 여기서 읽는다.
// 자주 안 바뀌므로 staleTime 을 길게 — 다만 단계 편집 mutation 들은 이 쿼리를 invalidate 한다.
export function usePipelineStages() {
  return useQuery({
    queryKey: crmKeys.pipelineStages(),
    queryFn: api.fetchPipelineStages,
    staleTime: 5 * 60_000,
  });
}

// L1 — 단계 정의(라벨/색/순서) 편집은 pipelineStages 정의와 그에 묶인 현황 카운트만
// 바꾼다(신청서·고객 레코드 자체는 안 건드림). 단계가 바뀌면 카운트의 버킷·라벨·순서가
// 달라지므로 stageCounts·pipelineCounts 도 함께 무효화한다.
function invalidatePipelineStageDefs(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: crmKeys.pipelineStages() });
  qc.invalidateQueries({ queryKey: crmKeys.stageCounts() });
  qc.invalidateQueries({ queryKey: crmKeys.pipelineCounts() });
}

export function useCreatePipelineStage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { label: string; color?: string }) =>
      api.createPipelineStage(input),
    onSuccess: () => invalidatePipelineStageDefs(qc),
  });
}

export function useUpdatePipelineStage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: number; label?: string; color?: string }) =>
      api.updatePipelineStage(input.id, { label: input.label, color: input.color }),
    onSuccess: () => invalidatePipelineStageDefs(qc),
  });
}

export function useReorderPipelineStages() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: number[]) => api.reorderPipelineStages(ids),
    onSuccess: () => invalidatePipelineStageDefs(qc),
  });
}

export function useDeletePipelineStage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.deletePipelineStage(id),
    onSuccess: () => invalidatePipelineStageDefs(qc),
  });
}

/** 현재 매장의 요금제 변경 가능일 계산식("D+185"·"M+3"). 미설정이면 null. */
export function usePlanChangeExpr() {
  return useQuery({
    queryKey: crmKeys.planChangeExpr(),
    queryFn: fetchPlanChangeExpr,
  });
}

// ─── Mutations ───────────────────────────────────────

/** 요금제 변경 가능일 계산식 저장. 성공 시 위젯들이 보는 쿼리를 갱신한다. */
export function useUpdatePlanChangeExpr() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (expr: string) => updatePlanChangeExpr(expr),
    onSuccess: (saved) =>
      qc.setQueryData(crmKeys.planChangeExpr(), saved),
  });
}

export function useCreateApplicationForCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (customerId: number) => {
      const result = await api.createApplicationForCustomer(customerId);
      // 새로 생성된 신청서에 한해 매장 기본값을 1회 자동 주입.
      // 디폴트 주입 실패는 best-effort — 신청서 자체는 이미 생성됐으므로 throw 하지 않는다.
      if (result.created) {
        try {
          const { formDefaults } = await fetchFormDefaults();
          const fields = RETAILER_FIELD_TABS.flatMap((t) =>
            t.groups.flatMap((g) => g.fields),
          );
          const { next, filledCount } = applyRetailerDefaults({
            current: {},
            formDefaults,
            fields,
            mode: "overwrite",
          });
          if (filledCount > 0) {
            await api.updateRetailerInfo(result.applicationFormId, next);
          }
        } catch (err) {
          // 콘솔에만 남기고 흐름은 계속.
          console.warn("매장 기본값 자동 주입 실패", err);
        }
      }
      return result;
    },
    // 새 신청서 생성 → 신청서 목록·by-customer(['crm','applications'] prefix) + 신청서 단건
    // (['crm','application'] 은 단수라 위 prefix 에 안 잡힘) + 현황 카운트. 새 신청서가 그
    // 고객의 파생 목록에도 잡히므로 ['crm','customer',id] 도 포함.
    onSuccess: (_result, customerId) => {
      qc.invalidateQueries({ queryKey: ["crm", "applications"] });
      qc.invalidateQueries({ queryKey: ["crm", "application"] });
      qc.invalidateQueries({ queryKey: crmKeys.stageCounts() });
      qc.invalidateQueries({ queryKey: crmKeys.pipelineCounts() });
      qc.invalidateQueries({ queryKey: crmKeys.customer(customerId) });
    },
  });
}

export function useUpdateApplicationStage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id: number;
      stage: api.CrmStage;
      stageDetail?: string;
    }) => api.updateApplicationStage(input.id, input.stage, input.stageDetail),
    // 신청서 상태 변경 → 목록·by-customer(['crm','applications'] prefix) + 단건
    // (['crm','application'] 은 단수라 별도) + 현황 카운트.
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["crm", "applications"] });
      qc.invalidateQueries({ queryKey: ["crm", "application"] });
      qc.invalidateQueries({ queryKey: crmKeys.stageCounts() });
      qc.invalidateQueries({ queryKey: crmKeys.pipelineCounts() });
    },
  });
}

// 고객유형 일괄 변경 — 개통대기 현황 선택 액션. customerType=null 이면 유형 비우기.
export function useBulkSetCustomerType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { ids: number[]; customerType: string | null }) =>
      api.bulkSetCustomerType(input.ids, input.customerType),
    // 고객유형 일괄 변경 → 고객 목록·단건(['crm','customers']·['crm','customer'])
    // + 신청서 목록(고객유형이 신청서 행에도 표시) + 현황 카운트.
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["crm", "customers"] });
      qc.invalidateQueries({ queryKey: ["crm", "customer"] });
      qc.invalidateQueries({ queryKey: ["crm", "applications"] });
      qc.invalidateQueries({ queryKey: ["crm", "application"] });
      qc.invalidateQueries({ queryKey: crmKeys.stageCounts() });
      qc.invalidateQueries({ queryKey: crmKeys.pipelineCounts() });
    },
  });
}

export function useFillCustomerSample() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.fillCustomerSample(id),
    // 고객 샘플값 채우기 → 고객 단건·목록 + 신청서 내용(샘플이 신청서 필드도 채움) + 카운트.
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["crm", "customers"] });
      qc.invalidateQueries({ queryKey: ["crm", "customer"] });
      qc.invalidateQueries({ queryKey: ["crm", "applications"] });
      qc.invalidateQueries({ queryKey: ["crm", "application"] });
      qc.invalidateQueries({ queryKey: crmKeys.stageCounts() });
      qc.invalidateQueries({ queryKey: crmKeys.pipelineCounts() });
    },
  });
}

export function useSoftDeleteCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.softDeleteCustomer(id),
    // 고객 삭제(휴지통行) → 고객 목록·단건 + 그 고객의 신청서들도 같이 빠지므로 신청서군
    // 전체(['crm','applications']·['crm','application']) + 현황 카운트.
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["crm", "customers"] });
      qc.invalidateQueries({ queryKey: ["crm", "customer"] });
      qc.invalidateQueries({ queryKey: ["crm", "applications"] });
      qc.invalidateQueries({ queryKey: ["crm", "application"] });
      qc.invalidateQueries({ queryKey: crmKeys.stageCounts() });
      qc.invalidateQueries({ queryKey: crmKeys.pipelineCounts() });
    },
  });
}

// 휴지통에서 복원 — 휴지통 목록과 본 목록이 동시 갱신. 복원되면 그 고객의 신청서들도
// 다시 보이므로 고객군 + 신청서군 + 카운트를 모두 무효화한다.
export function useRestoreCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.restoreCustomer(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["crm", "customers"] });
      qc.invalidateQueries({ queryKey: ["crm", "customer"] });
      qc.invalidateQueries({ queryKey: ["crm", "applications"] });
      qc.invalidateQueries({ queryKey: ["crm", "application"] });
      qc.invalidateQueries({ queryKey: crmKeys.stageCounts() });
      qc.invalidateQueries({ queryKey: crmKeys.pipelineCounts() });
    },
  });
}

// 신청서 단위 휴지통/복원 — 한 줄(=신청서 1건) 단위. 같은 고객의 다른 신청서엔 영향이 없다.
// 신청서군(목록·by-customer·단건) + 현황 카운트만 갱신. 고객 레코드 자체는 안 바뀐다.
export function useSoftDeleteApplication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.softDeleteApplication(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["crm", "applications"] });
      qc.invalidateQueries({ queryKey: ["crm", "application"] });
      qc.invalidateQueries({ queryKey: crmKeys.stageCounts() });
      qc.invalidateQueries({ queryKey: crmKeys.pipelineCounts() });
    },
  });
}

export function useRestoreApplication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.restoreApplication(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["crm", "applications"] });
      qc.invalidateQueries({ queryKey: ["crm", "application"] });
      qc.invalidateQueries({ queryKey: crmKeys.stageCounts() });
      qc.invalidateQueries({ queryKey: crmKeys.pipelineCounts() });
    },
  });
}

// 신청서 내용 복제 — 새 신청서가 목록·현황판 카운트에 함께 잡혀 (접수일 내림차순 정렬상)
// 맨 위에 바로 나타난다. 신청서군 + 카운트 무효화.
export function useDuplicateApplication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.duplicateApplication(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["crm", "applications"] });
      qc.invalidateQueries({ queryKey: ["crm", "application"] });
      qc.invalidateQueries({ queryKey: crmKeys.stageCounts() });
      qc.invalidateQueries({ queryKey: crmKeys.pipelineCounts() });
    },
  });
}

// task 254 / D8 — 1급 파이프라인 mutations (신청서 단위).
// 모두 신청서 상태/내용을 바꾸므로 신청서군(['crm','applications'] prefix + 단수
// ['crm','application']) + 현황 카운트(stageCounts·pipelineCounts) 만 무효화한다.
// 헬퍼로 모아 누락 위험을 없앤다.
function invalidateApplicationGroups(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ["crm", "applications"] });
  qc.invalidateQueries({ queryKey: ["crm", "application"] });
  qc.invalidateQueries({ queryKey: crmKeys.stageCounts() });
  qc.invalidateQueries({ queryKey: crmKeys.pipelineCounts() });
}

export function useAdvancePipeline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      applicationFormId: number;
      to: string;
      clearSettledAt?: boolean;
      restoreArchived?: boolean;
    }) =>
      api.advancePipeline(input.applicationFormId, input.to, {
        clearSettledAt: input.clearSettledAt,
        restoreArchived: input.restoreArchived,
      }),
    onSuccess: () => invalidateApplicationGroups(qc),
  });
}

// 일괄 전이 — 보관/복원 등 여러 건을 한 요청으로 처리. 성공 시 신청서군을 한 번만 무효화
// → for 루프로 단건 mutation 을 돌릴 때 매 건마다 목록이 재조회되던 낭비를 없앤다.
export function useBulkAdvancePipeline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { ids: number[]; to: string }) =>
      api.bulkAdvancePipeline(input.ids, input.to),
    onSuccess: () => invalidateApplicationGroups(qc),
  });
}

export function useCloseApplicationCard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (applicationFormId: number) => api.closeApplicationCard(applicationFormId),
    onSuccess: () => invalidateApplicationGroups(qc),
  });
}

export function useToggleApplicationIssue() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { applicationFormId: number; on: boolean; note?: string }) =>
      api.toggleApplicationIssue(input.applicationFormId, input.on, input.note),
    onSuccess: () => invalidateApplicationGroups(qc),
  });
}

// 폼링크 4단계 상태머신 전이 (#201·#202).
export function useConfirmApplication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.confirmApplication(id),
    onSuccess: () => invalidateApplicationGroups(qc),
  });
}

export function useUnconfirmApplication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.unconfirmApplication(id),
    onSuccess: () => invalidateApplicationGroups(qc),
  });
}

// ─── UI 라벨 (서버 _shared.ts 와 동일) ───────────────

export const STAGE_LABEL: Record<api.CrmStage, string> = {
  1: "부분입력 완료",
  2: "데이터 완료",
  3: "신분증 접수 완료",
  4: "포탈·서식 접수 완료",
  5: "문제 발생 처리 필요",
  6: "개통 완료 정산 전",
};

export function stageLabel(s: api.CrmStage): string {
  return STAGE_LABEL[s];
}

export const STAGE_CIRCLES: Record<api.CrmStage, string> = {
  1: "①",
  2: "②",
  3: "③",
  4: "④",
  5: "⑤",
  6: "⑥",
};

export const CARRIER_LABEL: Record<api.CrmCarrier, string> = {
  SKT: "SKT",
  KT: "KT",
  LGU: "LG U+",
};

// ─── 타입 재수출 (페이지는 hooks/crm 만 import) ──────

// ─── 정산 근거 (신청서 상세 패널) ─────────────────────────────────
//
// 정산 근거는 신청서 단위라 applicationFormId 로만 시작한다(customerId 가 한 박자 늦게
// 들어오는 문제와 무관). 좁은 settlementEvidence 키만 무효화해 전체 ["crm"] 갱신을 피한다.

export function useSettlementEvidence(applicationFormId: number | null) {
  return useQuery({
    queryKey: crmKeys.settlementEvidence(applicationFormId),
    queryFn: () => api.fetchSettlementEvidence(applicationFormId as number),
    enabled: !!applicationFormId,
  });
}

export function useCreateSettlementEvidence(applicationFormId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: api.SettlementEvidenceInput) =>
      api.createSettlementEvidence(applicationFormId as number, input),
    onSuccess: () =>
      qc.invalidateQueries({
        queryKey: crmKeys.settlementEvidence(applicationFormId),
      }),
  });
}

export function useUpdateSettlementEvidence(applicationFormId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: number; patch: api.SettlementEvidenceInput }) =>
      api.updateSettlementEvidence(
        applicationFormId as number,
        input.id,
        input.patch,
      ),
    onSuccess: () =>
      qc.invalidateQueries({
        queryKey: crmKeys.settlementEvidence(applicationFormId),
      }),
  });
}

export function useDeleteSettlementEvidence(applicationFormId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api.deleteSettlementEvidence(applicationFormId as number, id),
    onSuccess: () =>
      qc.invalidateQueries({
        queryKey: crmKeys.settlementEvidence(applicationFormId),
      }),
  });
}

/** 캡처 이미지 업로드 — 영구 키와 미리보기 URL 을 받는다. 저장은 생성/수정 mutation 에서. */
export function useUploadSettlementEvidenceImage(
  applicationFormId: number | null,
) {
  return useMutation({
    mutationFn: (file: File) =>
      api.uploadSettlementEvidenceImage(applicationFormId as number, file),
  });
}

/** 자동조회 — 버튼 클릭 시 추천을 끌어온다(사람이 수정·확정하므로 캐시하지 않고 매번 호출). */
export function useSettlementSuggest(applicationFormId: number | null) {
  return useMutation({
    mutationFn: () => api.fetchSettlementSuggest(applicationFormId as number),
  });
}

// ─── 개통 신청서 서류함 (스크린챗 티켓 #4) ─────────────────────────────

export function useApplicationDocuments(applicationFormId: number | null) {
  return useQuery({
    queryKey: crmKeys.applicationDocuments(applicationFormId),
    queryFn: () => api.fetchApplicationDocuments(applicationFormId as number),
    enabled: !!applicationFormId,
  });
}

/** 서류 1장 업로드 — 성공 시 목록만 무효화. */
export function useUploadApplicationDocument(applicationFormId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { file: File; docType: string }) =>
      api.uploadApplicationDocument(
        applicationFormId as number,
        input.file,
        input.docType,
      ),
    onSuccess: () =>
      qc.invalidateQueries({
        queryKey: crmKeys.applicationDocuments(applicationFormId),
      }),
  });
}

export function useDeleteApplicationDocument(applicationFormId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (documentId: number) =>
      api.deleteApplicationDocument(applicationFormId as number, documentId),
    onSuccess: () =>
      qc.invalidateQueries({
        queryKey: crmKeys.applicationDocuments(applicationFormId),
      }),
  });
}

/** 매장 서류 종류 목록 — 서류함 드롭다운 공유. */
export function useDocumentTypes() {
  return useQuery({
    queryKey: crmKeys.documentTypes(),
    queryFn: () => api.fetchDocumentTypes(),
    staleTime: 5 * 60 * 1000, // 자주 안 바뀌는 목록 — 5분 캐시
  });
}

/** 서류 종류 목록 저장 — "종류 추가" 시. 저장 후 목록 캐시 갱신. */
export function useUpdateDocumentTypes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (types: string[]) => api.updateDocumentTypes(types),
    onSuccess: (types) => qc.setQueryData(crmKeys.documentTypes(), types),
  });
}

export type {
  ApplicationDocument,
  ApplicationFilter,
  ApplicationForm,
  CrmCarrier,
  CrmCustomer,
  CrmPlanType,
  CrmStage,
  CustomerFilter,
  DashboardMemo,
  DealerMaster,
  PipelineBand,
  PipelineStage,
  PipelineStatus,
  SettlementEvidence,
  SettlementEvidenceInput,
  SettlementSuggestion,
  StageCount,
} from "../api/crm";
